import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { debounce } from 'lodash';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import '../assets/css/styles.min.css';
import Swal from 'sweetalert2';
import LoadingSpinner from '../components/LoadingSpinner';

// Extracted Modal Components with validation
const AddSliderModal = React.memo(({ isOpen, onClose, onAdd, newSlider, setNewSlider }) => {
  // Initialize hooks at the top level
  const [imageError, setImageError] = useState('');
  const [linkError, setLinkError] = useState('');
  
  if (!isOpen) return null;

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    
    // Handle file validation
    if (name === 'image' && files && files[0]) {
      // Validate image size (5MB max)
      if (files[0].size > 5 * 1024 * 1024) {
        setImageError('Image size should be less than 5MB');
        return;
      }
      
      // Validate image type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(files[0].type)) {
        setImageError('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
        return;
      }
      
      setImageError('');
      setNewSlider(prev => ({ ...prev, [name]: files[0] }));
    } 
    // Handle link validation
    else if (name === 'link') {
      try {
        new URL(value);
        setLinkError('');
      } catch (err) {
        if (value !== '') {
          setLinkError('Please enter a valid URL');
        } else {
          setLinkError('');
        }
      }
      setNewSlider(prev => ({ ...prev, [name]: value }));
    } 
    // Handle other fields
    else {
      setNewSlider(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Slider</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <form>
              <div className="mb-3">
                <label htmlFor="title" className="form-label">Title</label>
                <input 
                  type="text" 
                  className="form-control" 
                  name="title"
                  value={newSlider.title} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="mb-3">
                <label htmlFor="content" className="form-label">Content</label>
                <textarea 
                  className="form-control" 
                  name="content"
                  value={newSlider.content} 
                  onChange={handleFormChange} 
                  required
                  rows="3"
                ></textarea>
              </div>
              <div className="mb-3">
                <label htmlFor="image" className="form-label">Image</label>
                <input 
                  type="file" 
                  className="form-control" 
                  name="image"
                  accept="image/*"
                  onChange={handleFormChange} 
                  required 
                />
                {imageError && <div className="text-danger mt-1">{imageError}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="link" className="form-label">Link</label>
                <input 
                  type="url" 
                  className="form-control" 
                  name="link"
                  value={newSlider.link} 
                  onChange={handleFormChange} 
                  required 
                />
                {linkError && <div className="text-danger mt-1">{linkError}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="status" className="form-label">Status</label>
                <select 
                  className="form-select" 
                  name="status"
                  value={newSlider.status} 
                  onChange={handleFormChange} 
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={onAdd}
              disabled={!!imageError || !!linkError}
            >
              Save Slider
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const EditSliderModal = React.memo(({ isOpen, onClose, onEdit, editSlider, setEditSlider }) => {
  // Initialize hooks at the top level
  const [imageError, setImageError] = useState('');
  const [linkError, setLinkError] = useState('');
  
  if (!isOpen || !editSlider) return null;

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    
    // Handle file validation
    if (name === 'image' && files && files[0]) {
      // Validate image size (5MB max)
      if (files[0].size > 5 * 1024 * 1024) {
        setImageError('Image size should be less than 5MB');
        return;
      }
      
      // Validate image type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(files[0].type)) {
        setImageError('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
        return;
      }
      
      setImageError('');
      setEditSlider(prev => ({ ...prev, [name]: files[0] }));
    } 
    // Handle link validation
    else if (name === 'link') {
      try {
        new URL(value);
        setLinkError('');
      } catch (err) {
        if (value !== '') {
          setLinkError('Please enter a valid URL');
        } else {
          setLinkError('');
        }
      }
      setEditSlider(prev => ({ ...prev, [name]: value }));
    } 
    // Handle other fields
    else {
      setEditSlider(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Slider</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <form>
              <div className="mb-3">
                <label htmlFor="title" className="form-label">Title</label>
                <input 
                  type="text" 
                  className="form-control" 
                  name="title"
                  value={editSlider.title} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="mb-3">
                <label htmlFor="content" className="form-label">Content</label>
                <textarea 
                  className="form-control" 
                  name="content"
                  value={editSlider.content} 
                  onChange={handleFormChange} 
                  required
                  rows="3"
                ></textarea>
              </div>
              <div className="mb-3">
                <label htmlFor="image" className="form-label">Image</label>
                <input 
                  type="file" 
                  className="form-control" 
                  name="image"
                  accept="image/*"
                  onChange={handleFormChange} 
                />
                {imageError && <div className="text-danger mt-1">{imageError}</div>}
                {editSlider.image && typeof editSlider.image === 'string' && (
                  <div className="mt-2">
                    <img 
                      src={editSlider.image} 
                      alt="Current slider" 
                      loading="lazy"
                      style={{ maxHeight: '100px', maxWidth: '100%' }}
                    />
                    <p className="text-muted mt-1">Current image</p>
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="link" className="form-label">Link</label>
                <input 
                  type="url" 
                  className="form-control" 
                  name="link"
                  value={editSlider.link} 
                  onChange={handleFormChange} 
                  required 
                />
                {linkError && <div className="text-danger mt-1">{linkError}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="status" className="form-label">Status</label>
                <select 
                  className="form-select" 
                  name="status"
                  value={editSlider.status} 
                  onChange={handleFormChange} 
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={onEdit}
              disabled={!!imageError || !!linkError}
            >
              Update Slider
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

// Skeleton Loader Component
const SliderSkeleton = () => (
  <tr>
    <td colSpan="7">
      <div className="skeleton-loader" style={{ height: '60px', background: '#f0f0f0', borderRadius: '4px', animation: 'pulse 1.5s infinite' }}></div>
    </td>
  </tr>
);

const Sliders = () => {
  const [sliders, setSliders] = useState([]);
  const [newSlider, setNewSlider] = useState({ 
    title: '', 
    content: '', 
    image: null, 
    link: '', 
    status: 'active' 
  });
  const [editSlider, setEditSlider] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  // Server-side pagination
  const fetchSliders = useCallback(async (page = 1, perPage = 10, search = '') => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.codecircle.co/api/sliders?page=${page}&perPage=${perPage}&search=${encodeURIComponent(search)}`);
      if (!response.ok) {
        throw new Error('Failed to fetch sliders');
      }
      const data = await response.json();
      setSliders(data.items || data); // Handle both paginated and non-paginated responses
      setTotalItems(data.total || data.length);
      setError(null);
    } catch (error) {
      console.error('Error fetching sliders:', error);
      setError('An error occurred while fetching sliders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSliders(currentPage, itemsPerPage, searchQuery);
  }, [fetchSliders, currentPage, itemsPerPage, searchQuery]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setCurrentPage(1);
      fetchSliders(1, itemsPerPage, query);
    }, 300),
    [fetchSliders, itemsPerPage]
  );

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);

  const handleAddSlider = useCallback(async () => {
    const formData = new FormData();
    formData.append('title', newSlider.title);
    formData.append('content', newSlider.content);
    formData.append('image', newSlider.image);
    formData.append('link', newSlider.link);
    formData.append('status', newSlider.status);

    try {
      const response = await fetch('https://api.codecircle.co/api/sliders', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to add slider');
      }

      fetchSliders(currentPage, itemsPerPage, searchQuery);
      setNewSlider({ title: '', content: '', image: null, link: '', status: 'active' });
      setIsAddModalOpen(false);
      Swal.fire('Success!', 'Slider added successfully.', 'success');
    } catch (error) {
      console.error('Error adding slider:', error);
      Swal.fire('Error!', error.message || 'An error occurred while adding the slider.', 'error');
    }
  }, [newSlider, fetchSliders, currentPage, itemsPerPage, searchQuery]);

  const handleEditSlider = useCallback(async () => {
    if (!editSlider || !editSlider.id) {
      console.error('No slider selected for editing');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', editSlider.title);
      formData.append('content', editSlider.content);
      formData.append('link', editSlider.link);
      formData.append('status', editSlider.status);
      if (editSlider.image instanceof File) {
        formData.append('image', editSlider.image);
      }

      const response = await fetch(`https://api.codecircle.co/api/sliders/${editSlider.id}`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-HTTP-Method-Override': 'PUT',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update slider');
      }

      fetchSliders(currentPage, itemsPerPage, searchQuery);
      setEditSlider(null);
      setIsEditModalOpen(false);
      Swal.fire('Success!', 'Slider updated successfully.', 'success');
    } catch (error) {
      console.error('Error updating slider:', error);
      Swal.fire('Error!', error.message || 'An error occurred while updating the slider.', 'error');
    }
  }, [editSlider, fetchSliders, currentPage, itemsPerPage, searchQuery]);

  const openEditModal = useCallback((slider) => {
    setEditSlider({
      ...slider,
      image: slider.image // Keep the image URL for preview
    });
    setIsEditModalOpen(true);
  }, []);

  const deleteSlider = useCallback(async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`https://api.codecircle.co/api/sliders/${id}/soft-delete`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error('Failed to delete slider');
        }

        fetchSliders(currentPage, itemsPerPage, searchQuery);
        Swal.fire('Deleted!', 'Slider has been deleted.', 'success');
      } catch (error) {
        console.error('Error deleting slider:', error);
        Swal.fire('Error!', error.message || 'An error occurred while deleting the slider.', 'error');
      }
    }
  }, [fetchSliders, currentPage, itemsPerPage, searchQuery]);

  // Pagination calculation
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((e) => {
    const newItemsPerPage = Number(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);

  // Memoized current sliders
  const currentSliders = useMemo(() => sliders, [sliders]);

  return (
    <div className="page-wrapper" id="main-wrapper" data-layout="vertical" data-navbarbg="skin6" data-sidebartype="full" data-sidebar-position="fixed" data-header-position="fixed">
      <Sidebar />

      <div className="body-wrapper">
        <Header />

        <div className="container-fluid">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title fw-semibold mb-4">Sliders Management</h5>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* Search and Controls Section */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="search-box">
                      <div className="input-group" style={{ width: '250px' }}>
                        <span className="input-group-text border-primary bg-primary">
                          <i className="fas fa-search text-white"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-primary"
                          placeholder="Search sliders..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                        />
                      </div>
                    </div>
                    <select
                      className="form-select border-primary"
                      style={{ width: '100px' }}
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{ borderRadius: '15px', padding: '8px 16px', fontSize: '14px' }}
                    onClick={() => setIsAddModalOpen(true)}
                  >
                    Add Slider
                  </button>
                </div>

                <div className="table-responsive">
                  <table className="table text-nowrap align-middle mb-0">
                    <thead>
                      <tr className="border-2 border-bottom border-primary border-0">
                        <th scope="col" className="ps-0">ID</th>
                        <th scope="col" className="ps-0">Title</th>
                        <th scope="col" className="ps-0">Content</th>
                        <th scope="col" className="ps-0">Image</th>
                        <th scope="col" className="ps-0">Link</th>
                        <th scope="col" className="text-center">Status</th>
                        <th scope="col" className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="table-group-divider">
                      {loading ? (
                        // Show skeleton loaders when loading
                        Array(itemsPerPage).fill(0).map((_, index) => <SliderSkeleton key={index} />)
                      ) : currentSliders.length > 0 ? (
                        currentSliders.map(slider => (
                          <tr key={slider.id}>
                            <th scope="row" className="ps-0 fw-medium">{slider.id}</th>
                            <td className="fw-medium">{slider.title}</td>
                            <td className="fw-medium">{slider.content}</td>
                            <td className="fw-medium">
                              <img 
                                src={slider.image || '/default-slider.png'} 
                                alt={slider.title} 
                                width="100"
                                loading="lazy" // Add lazy loading
                                style={{ maxHeight: '60px', objectFit: 'contain' }}
                                onError={(e) => {
                                  e.target.src = '/default-slider.png';
                                  e.target.onerror = null;
                                }}
                              />
                            </td>
                            <td className="fw-medium">
                              <a href={slider.link} target="_blank" rel="noopener noreferrer">
                                Link
                              </a>
                            </td>
                            <td className="text-center fw-medium">
                              <span className={`badge ${slider.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                {slider.status.charAt(0).toUpperCase() + slider.status.slice(1)}
                              </span>
                            </td>
                            <td className="text-center fw-medium">
                              <button 
                                className="btn btn-sm btn-primary me-2"
                                onClick={() => openEditModal(slider)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button 
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteSlider(slider.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-3">No sliders found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {!loading && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="text-muted fs-13">
                      Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    </div>
                    <nav>
                      <ul className="pagination pagination-primary mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}>
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        </li>
                        {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                          // Show at most 5 page buttons, centered around current page
                          let pageToShow;
                          if (totalPages <= 5) {
                            pageToShow = i + 1;
                          } else if (currentPage <= 3) {
                            pageToShow = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageToShow = totalPages - 4 + i;
                          } else {
                            pageToShow = currentPage - 2 + i;
                          }
                          
                          return (
                            <li key={i} className={`page-item ${currentPage === pageToShow ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => handlePageChange(pageToShow)}>
                                {pageToShow}
                              </button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}>
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <AddSliderModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddSlider}
          newSlider={newSlider}
          setNewSlider={setNewSlider}
        />

        <EditSliderModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onEdit={handleEditSlider}
          editSlider={editSlider}
          setEditSlider={setEditSlider}
        />
      </div>
    </div>
  );
};

// Add animation styles for skeleton
const skeletonStyles = `
@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}
`;

// Inject styles
const style = document.createElement('style');
style.innerHTML = skeletonStyles;
document.head.appendChild(style);

export default React.memo(Sliders);